import type { CSSProperties } from 'react';
import { COULEURS } from '../../app/styles.js';
import {
  A_DEFINIR,
  A_DETERMINER,
  BADGE_MASQUEE,
  CANAUX,
  MANQUANT as TEXTE_MANQUANT,
  NON_COMMUNIQUE,
  SITUATIONS_SUCCESSION,
  STATUTS_DEVOLUTION,
} from './libelles.js';
import { ordreDeLecture } from './clavier.js';
import { nomAffiche, quotePart } from './modele.js';
import type { DonneesArbre, Personne } from './types.js';

export const MANQUANT = TEXTE_MANQUANT;

export interface Colonne {
  libelle: string;
  /** Information à trouver : elle compte dans le taux de complétude. */
  requise: boolean;
}

export interface Ligne {
  id: string;
  cellules: Record<string, string>;
  /** Colonnes dont l'information manque. */
  manquantes: string[];
  /** Colonnes dont la coordonnée est masquée pour la famille (badge réservé à l'équipe). */
  masquees: string[];
}

/** Colonnes du tableau des vivants, fixées par la réunion du 16/10 (#11). Le même ordre sert à l'export du notaire. */
export const COLONNES_VIVANTS: readonly Colonne[] = [
  { libelle: 'Nom et prénoms', requise: false },
  { libelle: 'Naissance', requise: true },
  { libelle: 'Adresse', requise: true },
  { libelle: 'Profession', requise: true },
  { libelle: 'Situation', requise: true },
  { libelle: 'Téléphone', requise: true },
  { libelle: 'E-mail', requise: true },
  { libelle: 'Canal préféré', requise: true },
  { libelle: 'Droits', requise: false },
];

const COLONNES_DEFUNTS = ['Défunt', 'Décès', 'Situation'] as const;

const dateFr = (iso: string): string => iso.split('-').reverse().join('/');

function naissance(p: Personne): string | null {
  const { value, precision } = p.birthDate;
  const date = value && precision !== 'inconnue' ? (precision === 'jour' ? dateFr(value) : value.slice(0, 4)) : null;
  const morceaux = [date, p.birthPlace].filter((x): x is string => Boolean(x));
  return morceaux.length > 0 ? morceaux.join(' · ') : null;
}

/** À qui s'adresse le tableau : l'équipe voit tout, le notaire ne voit que les coordonnées ouvertes au notaire ou à la famille. */
export type Destinataire = 'equipe' | 'notaire';

const ouverte = (c: Personne['contacts'][number], pour: Destinataire): boolean =>
  pour === 'equipe' || c.visibility === 'notaire' || c.visibility === 'famille';

/** Coordonnée connue du Cabinet mais pas montrée à la famille : elle porte un badge dans le tableau de l'équipe. */
const masqueePourFamille = (c: Personne['contacts'][number]): boolean => c.maskedForFamily || c.visibility === 'equipe';

interface Coordonnee {
  valeur: string;
  masquee: boolean;
}

/** Première coordonnée du type voulu et visible pour le destinataire ; `retenue` quand elle existe mais lui est fermée. */
const premiereCoordonnee = (p: Personne, types: readonly string[], pour: Destinataire): Coordonnee | null => {
  let retenue = false;
  for (const t of types) {
    for (const c of p.contacts.filter((x) => x.type === t)) {
      if (ouverte(c, pour)) return { valeur: c.value, masquee: pour === 'equipe' && masqueePourFamille(c) };
      retenue = true;
    }
  }
  return retenue ? { valeur: NON_COMMUNIQUE, masquee: false } : null;
};

function ligneVivant(d: DonneesArbre, p: Personne, pour: Destinataire): Ligne {
  const comp = d.complements?.[p.id];
  const canal = p.preferences.preferredChannel;
  const coordonnees: Record<string, Coordonnee | null> = {
    Adresse: premiereCoordonnee(p, ['address'], pour),
    Téléphone: premiereCoordonnee(p, ['phone', 'whatsapp'], pour),
    'E-mail': premiereCoordonnee(p, ['email'], pour),
  };
  const brut: Record<string, string | null> = {
    'Nom et prénoms': `${p.lastName} ${p.firstNames}`.trim(),
    Naissance: naissance(p),
    Adresse: coordonnees['Adresse']?.valeur ?? null,
    Profession: comp?.profession || null,
    Situation: comp?.situation || null,
    Téléphone: coordonnees['Téléphone']?.valeur ?? null,
    'E-mail': coordonnees['E-mail']?.valeur ?? null,
    'Canal préféré': canal ? CANAUX[canal] : null,
    Droits: quotePart(d, p.id),
  };
  const cellules: Record<string, string> = {};
  const manquantes: string[] = [];
  for (const c of COLONNES_VIVANTS) {
    const v = brut[c.libelle];
    if (v) cellules[c.libelle] = v;
    else if (c.libelle === 'Droits') cellules[c.libelle] = A_DETERMINER;
    else {
      cellules[c.libelle] = c.libelle === 'Canal préféré' ? A_DEFINIR : MANQUANT;
      manquantes.push(c.libelle);
    }
  }
  const masquees = Object.keys(coordonnees).filter((k) => coordonnees[k]?.masquee);
  return { id: p.id, cellules, manquantes, masquees };
}

/** Les héritiers vivants identifiés (ni défunts, ni hypothèses), dans l'ordre de lecture de l'arbre. */
export function lignesVivants(d: DonneesArbre, pour: Destinataire = 'equipe'): Ligne[] {
  const rang = new Map(ordreDeLecture(d).map((id, i) => [id, i] as const));
  return d.personnes
    .filter((p) => p.vitalStatus === 'vivant' && !p.isHypothesis)
    .sort((a, b) => (rang.get(a.id) ?? 0) - (rang.get(b.id) ?? 0))
    .map((p) => ligneVivant(d, p, pour));
}

/** Taux de complétude : informations renseignées sur informations à trouver, arrondi à l'entier. */
export function completude(d: DonneesArbre): number {
  const lignes = lignesVivants(d);
  const requises = COLONNES_VIVANTS.filter((c) => c.requise).length;
  const total = lignes.length * requises;
  if (total === 0) return 0;
  const manquantes = lignes.reduce((n, l) => n + l.manquantes.length, 0);
  return Math.round((100 * (total - manquantes)) / total);
}

/** Les défunts, dans l'ordre des décès : cet ordre conditionne le calcul des parts. */
export function lignesDefunts(d: DonneesArbre): Ligne[] {
  return d.personnes
    .filter((p) => p.vitalStatus === 'decede')
    .sort((a, b) => (a.deathDate ?? '9999').localeCompare(b.deathDate ?? '9999'))
    .map((p) => {
      const succession = d.successions.find((s) => s.deceasedPersonId === p.id);
      const deces = [p.deathDate ? dateFr(p.deathDate) : 'date inconnue', p.deathPlace].filter(Boolean).join(' · ');
      return {
        id: p.id,
        cellules: {
          Défunt: nomAffiche(p),
          Décès: deces,
          Situation: SITUATIONS_SUCCESSION[succession ? succession.status : 'aucune'],
        },
        manquantes: [],
        masquees: [],
      };
    });
}

const styleTable: CSSProperties = { borderCollapse: 'collapse', width: '100%', fontSize: 14 };
const styleCellule: CSSProperties = {
  border: `1px solid ${COULEURS.bordure}`,
  padding: '8px 10px',
  textAlign: 'left',
  verticalAlign: 'top',
};
const styleBadge: CSSProperties = {
  display: 'inline-block',
  marginLeft: 6,
  padding: '0 6px',
  borderRadius: 999,
  border: `1px solid ${COULEURS.bordure}`,
  color: COULEURS.ardoise,
  fontSize: 12,
  fontWeight: 400,
};
const styleLegende: CSSProperties = { textAlign: 'left', fontWeight: 600, color: COULEURS.marine, padding: '8px 0' };

/** Tableau des vivants : réservé à l'équipe, repris dans l'export du notaire (#12). */
export function TableauVivants({ donnees }: { donnees: DonneesArbre }) {
  const lignes = lignesVivants(donnees);
  return (
    <section data-tableau="vivants" style={{ overflowX: 'auto', margin: '24px 0' }}>
      <table style={styleTable}>
        <caption style={styleLegende}>
          Tableau des vivants complet à {completude(donnees)} % · réservé à l’équipe, repris dans l’export du notaire
        </caption>
        <thead>
          <tr>
            {COLONNES_VIVANTS.map((c) => (
              <th key={c.libelle} scope="col" style={styleCellule}>
                {c.libelle}
                {c.libelle === 'Droits' ? (
                  <span data-statut-devolution={donnees.devolution.status} style={styleBadge}>
                    {STATUTS_DEVOLUTION[donnees.devolution.status].mention}
                  </span>
                ) : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.length === 0 ? (
            <tr>
              <td colSpan={COLONNES_VIVANTS.length} style={styleCellule}>
                Aucun héritier vivant n’est encore identifié dans ce dossier.
              </td>
            </tr>
          ) : (
            lignes.map((l) => (
              <tr key={l.id} data-vivant={l.id}>
                {COLONNES_VIVANTS.map((c) => {
                  const manque = l.manquantes.includes(c.libelle);
                  return (
                    <td
                      key={c.libelle}
                      data-manquant={manque ? 'true' : undefined}
                      style={{ ...styleCellule, color: manque ? COULEURS.ambre : 'inherit' }}
                    >
                      {l.cellules[c.libelle]}
                      {l.masquees.includes(c.libelle) ? (
                        <span data-masquee="famille" style={styleBadge}>
                          {BADGE_MASQUEE}
                        </span>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}

/** Tableau des défunts, séparé de celui des vivants ; chaque ligne ouvre l'acte et la situation du défunt (#13). */
export function TableauDefunts({ donnees, onOuvrir }: { donnees: DonneesArbre; onOuvrir?: (id: string) => void }) {
  const lignes = lignesDefunts(donnees);
  return (
    <section data-tableau="defunts" style={{ overflowX: 'auto', margin: '24px 0' }}>
      <table style={styleTable}>
        <caption style={styleLegende}>Tableau des défunts · un clic sur un nom ouvre l’acte et la situation</caption>
        <thead>
          <tr>
            {COLONNES_DEFUNTS.map((c) => (
              <th key={c} scope="col" style={styleCellule}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={l.id}>
              <th scope="row" style={styleCellule}>
                <button
                  type="button"
                  data-defunt={l.id}
                  onClick={() => onOuvrir?.(l.id)}
                  style={{
                    minHeight: 44,
                    minWidth: 44,
                    background: 'none',
                    border: 'none',
                    color: COULEURS.marine,
                    font: 'inherit',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {l.cellules['Défunt']}
                </button>
              </th>
              <td style={styleCellule}>{l.cellules['Décès']}</td>
              <td style={styleCellule}>{l.cellules['Situation']}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
